import { useState, useMemo } from "react";
import { api } from "convex/_generated/api";
import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import { useTranslation } from "@/packages/utils";
import { AuditSelectSheet } from "@/components/audits/AuditSelectSheet";
import { ReportSideSheet } from "@/components/audits/overview/ReportSideSheet";
import type { AuditId, AuditStatus, Id } from "@/packages/types";
import { useAudits } from "@/hooks/use-audits";

interface CreateReportSheetProps {
  open: boolean;
  onClose: () => void;
}

interface SelectedAudit {
  id: AuditId;
  displayName: string;
  status: AuditStatus;
  reportId: Id<"reports"> | null;
}

export function CreateReportSheet({ open, onClose }: CreateReportSheetProps) {
  const { t } = useTranslation(["reports"]);
  const [selectedAudit, setSelectedAudit] = useState<SelectedAudit | null>(
    null,
  );

  const { audits } = useAudits();
  const { data: rawReports } = useWorkspaceQuery(api.reports.list, {});

  const reportByAudit = useMemo(() => {
    const map = new Map<string, Id<"reports">>();
    if (rawReports) {
      for (const r of rawReports) {
        map.set(r.auditId, r.id as Id<"reports">);
      }
    }
    return map;
  }, [rawReports]);

  const handleAuditSelect = (auditId: AuditId) => {
    const audit = audits?.find((a) => a.id === auditId);
    if (!audit) return;
    setSelectedAudit({
      id: audit.id,
      displayName: audit.displayName,
      status: audit.status,
      reportId: reportByAudit.get(audit.id) ?? null,
    });
  };

  const handleBack = () => {
    setSelectedAudit(null);
  };

  const handleClose = () => {
    setSelectedAudit(null);
    onClose();
  };

  if (selectedAudit) {
    return (
      <ReportSideSheet
        reportId={selectedAudit.reportId}
        auditId={selectedAudit.id}
        auditName={selectedAudit.displayName}
        auditStatus={selectedAudit.status}
        open={open}
        onClose={handleBack}
      />
    );
  }

  return (
    <AuditSelectSheet
      open={open}
      onClose={handleClose}
      title={t("reports:create.title")}
      description={t("reports:create.description")}
      onSelect={handleAuditSelect}
      closeOnSelect={false}
    />
  );
}
